import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, DownloadIcon, Undo2Icon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { diffWords, downloadTranscript } from '../../utils/transcripts';
import { TranscriptVersions } from '../../components/TranscriptVersions';
import { TranscriptMarkup } from '../../components/TranscriptMarkup';
import { TranscriptEditor } from '../../components/TranscriptEditor';

/** A transcript, open for light corrections. Read, fix, undo, keep. */
export function Transcript() {
  const {
    activeDeliverable,
    goClient,
    saveTranscript,
    notify
  } = usePortal();
  const [text, setText] = useState(activeDeliverable?.transcript ?? '');
  /** Local edit history, so Undo works before and after saving. */
  const [history, setHistory] = useState<string[]>([]);
  /** Which saved version the box below is showing. */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Bumped whenever the live editor must be rebuilt from the saved state. */
  const [resetKey, setResetKey] = useState(0);
  const saved = activeDeliverable?.transcript ?? '';
  const revisions = activeDeliverable?.transcriptRevisions ?? [];
  const original = revisions[0];
  const latest = revisions[revisions.length - 1];
  useEffect(() => {
    setText(saved);
    setHistory([]);
    setSelectedId(null);
    setResetKey(key => key + 1);
  }, [activeDeliverable?.id, saved]);
  const selected = revisions.find(entry => entry.id === selectedId) ?? latest;
  const isOriginal = Boolean(selected) && selected?.id === original?.id;
  const viewingLatest = !selected || selected.id === latest?.id;

  /* Every version is compared against the original, so the markup carries the
     full record of what has changed since Portal delivered it. */
  const liveParts = useMemo(() => diffWords(original?.text ?? '', text),
  // Rebuilt only on save, undo or reopening; live typing must not
  // re-render the document under the caret.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [resetKey]);
  const pastParts = useMemo(() => selected && !isOriginal ? diffWords(original?.text ?? '', selected.text) : [], [original?.text, selected, isOriginal]);
  if (!activeDeliverable) return null;
  const item = activeDeliverable;
  const dirty = text !== saved;
  function change(next: string) {
    setHistory(stack => [...stack.slice(-99), text]);
    setText(next);
  }
  function undo() {
    if (!history.length) return;
    setText(history[history.length - 1]);
    setHistory(stack => stack.slice(0, -1));
    setResetKey(key => key + 1);
  }
  return <div data-id="element-659" data-mp="15mx8wotbwpo0">
      <button type="button" onClick={() => goClient('episodes')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-660" data-mp="15mnkh5nxmqkf">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-661" data-mp="3qcmo52xy2dh8" />
        All Deliverables
      </button>

      <h1 className="mt-10 max-w-3xl font-display text-4xl leading-[1.02] tracking-tight sm:text-6xl" data-id="element-662" data-mp="20vr5jd50u7s5">
        {item.name}
      </h1>

      <div className="mt-10 max-w-4xl" data-id="element-663" data-mp="2avhzndpo6krp">
        <TranscriptVersions revisions={revisions} selectedId={selected?.id ?? ''} onSelect={id => {
        setSelectedId(id);
        // Returning to the latest edit reopens the live working document.
        if (id === latest?.id) setResetKey(key => key + 1);
      }} data-id="element-664" data-mp="2augn14t09q7v" />
      </div>

      <div className="mt-12 max-w-4xl" data-id="element-665" data-mp="2tmixtziyuca7">
        {viewingLatest ? <>
            <span className="label block text-graphite" data-id="element-666" data-mp="2ylcbbry963ad">Transcript</span>
            <TranscriptEditor parts={liveParts} resetKey={resetKey} onChange={change} data-id="element-667" data-mp="2ykkph8ugjjxa" />

            <div className="mt-9 flex flex-wrap items-center gap-6" data-id="element-668" data-mp="z9t3jgoor12a">
              <button type="button" onClick={() => {
            saveTranscript(item.id, text);
            setHistory([]);
            setSelectedId(null);
            setResetKey(key => key + 1);
            notify('Transcript saved');
          }} disabled={!dirty} className="label border border-ink px-7 py-4 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper disabled:border-rule disabled:text-mute disabled:hover:bg-transparent disabled:hover:text-mute" data-id="element-669" data-mp="146jiu1so1smn">
                Save
              </button>
              <button type="button" onClick={undo} disabled={history.length === 0} className="label flex items-center gap-3 text-graphite transition-colors duration-150 ease-out hover:text-ink disabled:text-mute/50 disabled:hover:text-mute/50" data-id="element-670" data-mp="270p1c51si1nu">
                <Undo2Icon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-671" data-mp="1s7lky0zxptyp" />
                Undo
              </button>
              <button type="button" onClick={() => downloadTranscript(item.name, text)} className="label flex items-center gap-3 border border-rule px-7 py-4 text-graphite transition-colors duration-150 ease-out hover:border-ink hover:text-ink" data-id="element-672" data-mp="1e4pih4uad6i0">
                Download
                <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-673" data-mp="1964jf8qrssv5" />
              </button>
              {dirty && <p className="label-sm m-0 text-mute" data-id="element-674" data-mp="1ww8ir3wxe6gc">Unsaved changes</p>}
            </div>
          </> : isOriginal ? (/* The original upload, exactly as Portal delivered it — no markup. */
      <div className="whitespace-pre-wrap border border-rule bg-paperDeep px-6 py-6 font-display text-xl leading-relaxed text-charcoal sm:px-9 sm:py-8 sm:text-2xl" data-id="element-675" data-mp="1mw82jqucs6gd">
            {selected?.text}
          </div>) : <TranscriptMarkup parts={pastParts} data-id="element-676" data-mp="bjxc3r330mf3" />}
      </div>
    </div>;
}